/*
 * TerriSTORY®
 *
 © Copyright 2022 AURA-EE
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * A copy of the GNU Affero General Public License should be present along
 * with this program at the root of current repository. If not, see
 * http://www.gnu.org/licenses/.
 */

import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Api from "../../Controllers/Api.js";
import config from "../../settings.js";
import { buildRegionUrl } from "../../utils.js";
import "../../style/national/dataimport.css";
import "bootstrap/dist/css/bootstrap.min.css";
const DataImport = props => {
  var _location$state, _metadata$table_name;
  const inputFileRef = useRef(null);
  const [fichierDonnees, setFichierDonnees] = useState(undefined);
  const [metadata, setMetadata] = useState({});
  const [status, setStatus] = useState(undefined);
  const [responseError, setResponseError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState(undefined);
  const [unit, setUnit] = useState(undefined);
  const [enabledModules, setEnabledModules] = useState({});
  const location = useLocation();
  const {
    region
  } = useParams();
  const params = (_location$state = location.state) === null || _location$state === void 0 ? void 0 : _location$state.params;
  useEffect(() => {
    // Build URL
    let url = buildRegionUrl(config.api_analysis_data_categories_national_url, "national").replace("#territory#", region).replace("#table_name#", params.table);
    Api.callApi(url, null, "GET").then(response => {
      setCategories(response.categories);
      setUnit(response.data_unit);
    }).catch(e => console.error(e));
    url = buildRegionUrl(config.api_national_import_metadata, region) + "/" + params.table;
    Api.callApi(url, null, "GET").then(response => {
      setMetadata(response);
    }).catch(e => console.error(e));
  }, [region, params.table]);
  const renderTips = () => {
    const renderCategoryCard = category => {
      if (metadata.categories && !metadata.categories.includes(category)) {
        return "";
      }
      return /*#__PURE__*/React.createElement("div", {
        className: "card",
        key: category
      }, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement("code", null, category)), /*#__PURE__*/React.createElement("details", {
        className: "tsdetails"
      }, /*#__PURE__*/React.createElement("summary", null, "d\xE9tail des modalit\xE9s"), /*#__PURE__*/React.createElement("ul", null, categories[category].map(item => /*#__PURE__*/React.createElement("li", {
        key: item.modalite_id
      }, /*#__PURE__*/React.createElement("code", null, item.modalite))))));
    };
    const renderRequirement = _ref => {
      let [requirement, param] = _ref;
      return /*#__PURE__*/React.createElement("p", {
        key: requirement
      }, "Pour le", " ", /*#__PURE__*/React.createElement("strong", null, requirement === "strategy" ? "module de stratégie" : "simulateur mobilité"), param ? Object.entries(param).map(_ref2 => {
        let [category, modalities] = _ref2;
        return /*#__PURE__*/React.createElement("span", {
          key: category
        }, " ", ": colonne ", /*#__PURE__*/React.createElement("em", null, category), " (pour les modalit\xE9s", " ", /*#__PURE__*/React.createElement("em", null, modalities.join(", "), ")"));
      }) : "");
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "import-tips"
    }, /*#__PURE__*/React.createElement("h3", null, "Consignes g\xE9n\xE9rales"), /*#__PURE__*/React.createElement("p", null, "Le fichier de donn\xE9es doit \xEAtre au ", /*#__PURE__*/React.createElement("strong", null, "format CSV"), ", s\xE9par\xE9 par des ", /*#__PURE__*/React.createElement("strong", null, "points-virgules"), ". Le s\xE9parateur d\xE9cimal est le point. La ", /*#__PURE__*/React.createElement("strong", null, "premi\xE8re ligne"), " doit contenir les ", /*#__PURE__*/React.createElement("strong", null, "ent\xEAtes"), " avec les noms de colonnes sp\xE9cifi\xE9s ci-dessous (l'ordre n'est pas important)."), /*#__PURE__*/React.createElement("h3", null, "Colonnes attendues"), /*#__PURE__*/React.createElement("div", {
      className: "cards"
    }, /*#__PURE__*/React.createElement("div", {
      className: "card"
    }, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement("code", null, "annee")), "le mill\xE9sime des donn\xE9es"), /*#__PURE__*/React.createElement("div", {
      className: "card"
    }, /*#__PURE__*/React.createElement("strong", null, /*#__PURE__*/React.createElement("code", null, "valeur")), unit && `en ${unit}`), categories && Object.keys(categories).map(renderCategoryCard)), metadata.requirements && Object.keys(metadata.requirements).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h4", null, "Donn\xE9es n\xE9cessaires"), Object.entries(metadata.requirements).map(renderRequirement)), metadata.optional && Object.keys(metadata.optional).length > 0 && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h4", null, "Donn\xE9es optionnelles"), Object.entries(metadata.optional).map(renderRequirement)), metadata.template_file && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", null, "Exemple de table attendue"), /*#__PURE__*/React.createElement("p", null, "Vous trouverez \xE0 l'adresse", " ", /*#__PURE__*/React.createElement("a", {
      target: "_blank",
      rel: "noopener noreferrer",
      href: "/uploads/" + metadata.template_file
    }, "suivante"), " ", "un ", /*#__PURE__*/React.createElement("strong", null, "document Excel"), " vous permettant de remplir et de g\xE9n\xE9rer automatiquement le fichier csv attendu dans le bon format. Vous n'avez plus qu'\xE0 remplir la feuille appel\xE9e \xAB\xA0", /*#__PURE__*/React.createElement("em", null, "Onglet \xE0 remplir"), "\xA0\xBB et la feuille \xAB\xA0", /*#__PURE__*/React.createElement("em", null, "CSV"), "\xA0\xBB pourra \xEAtre enregistr\xE9e comme fichier CSV puis import\xE9e ici. Plus d'informations sont disponibles dans l'onglet \xAB\xA0Mode d'emploi\xA0\xBB.")));
  };
  const renderEnabledModules = () => {
    if (!enabledModules) return "";
    const nbEnabledModules = Object.values(enabledModules).filter(Boolean).length;
    if (nbEnabledModules === 0) {
      return /*#__PURE__*/React.createElement("p", null, "Aucun module activ\xE9 pour l'instant, certaines donn\xE9es manquent encore.");
    }
    return /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, nbEnabledModules, " module", nbEnabledModules > 1 && "s", " activ\xE9", nbEnabledModules > 1 && "s"), " ", ": ", enabledModules.simulator && "le simulateur", nbEnabledModules > 1 && ", ", enabledModules.territorial_strategy && "le module de stratégie");
  };
  const renderActionButtons = () => {
    let submitted = "";
    let loadingMsg = "";
    if (status) {
      loadingMsg = "";
      submitted = /*#__PURE__*/React.createElement("div", {
        className: "form-group centered-row"
      }, /*#__PURE__*/React.createElement("label", {
        className: `alert alert-${responseError ? "warning" : "success"} col-sm-4`
      }, status, renderEnabledModules()));
    }
    if (isLoading) {
      submitted = "";
      loadingMsg = /*#__PURE__*/React.createElement("div", {
        className: "form-group centered-row"
      }, /*#__PURE__*/React.createElement("label", {
        className: `col-sm-4`
      }, /*#__PURE__*/React.createElement("div", {
        className: "loader-data"
      })));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "import_form"
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      ref: inputFileRef,
      onChange: e => onFichierDataSelection(e)
    }), /*#__PURE__*/React.createElement("button", {
      className: "tsbtn warning",
      onClick: () => updateTable(params.table),
      disabled: isLoading
    }, "Mettre \xE0 jour"), /*#__PURE__*/React.createElement("br", null), loadingMsg, /*#__PURE__*/React.createElement("br", null), submitted);
  };
  const onFichierDataSelection = event => {
    setFichierDonnees(event.target.files[0]);
  };
  const resetInputFile = () => {
    inputFileRef.current.value = null;
  };
  const updateTable = table => {
    // Check if a data file is selected
    if (!fichierDonnees) {
      setResponseError(true);
      setStatus("Vous devez choisir un fichier de données");
      return;
    }
    const userConfirmed = window.confirm("Attention, cette opération n'est pas réversible. Voulez-vous continuer ?");
    if (!userConfirmed) {
      return;
    }

    // Prepare the form data to be sent
    const formData = new FormData();
    formData.append("file", fichierDonnees);

    // Build the API URL
    const baseUrl = buildRegionUrl(config.api_analysis_data_national_url, "national").replace("#territory#", props.parentApi.data.region);
    const url = `${baseUrl}/${table}`;

    // Set loading state
    setIsLoading(true);

    // Make the API call
    Api.callApi(url, formData, "PUT", "default").then(response => {
      // Handle successful response
      setResponseError(false);
      setStatus(response.message);
      resetInputFile();
      update_data();
    }).catch(error => {
      // Handle error response
      setResponseError(true);
      setStatus(error.message);
    }).finally(() => {
      // Reset loading state
      setIsLoading(false);
    });
  };
  const update_data = () => {
    // Build the API URL
    const url = buildRegionUrl(config.api_activate_territorial_strategy_simulator, "national").replace("#territory#", region);

    // Make the API call
    Api.callApi(url, null, "GET", "default").then(response => {
      // Handle successful response
      setEnabledModules(response);
    }).catch(error => {
      // Handle error response
      console.error("Error updating data:", error);
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Link, {
    className: "btn btn-light return-button",
    to: "/national/" + region + "/import"
  }, /*#__PURE__*/React.createElement("i", {
    className: "bi bi-arrow-left"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "container-import"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "tstitle"
  }, "Import des donn\xE9es - ", (_metadata$table_name = metadata === null || metadata === void 0 ? void 0 : metadata.table_name) !== null && _metadata$table_name !== void 0 ? _metadata$table_name : params.table), (metadata === null || metadata === void 0 ? void 0 : metadata.description) && /*#__PURE__*/React.createElement("p", null, metadata === null || metadata === void 0 ? void 0 : metadata.description), renderActionButtons()), /*#__PURE__*/React.createElement("div", {
    className: "container-import"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "tstitle"
  }, "Aide"), renderTips()));
};
export default DataImport;